/**
 * The BASICS surface: the founder's "edit setup as its own section". It hosts the
 * full setup/config editor (the "About this newsletter" ConfigEditor behind the Edit
 * setup disclosure - every wizard field), the schedule/cadence panel, and the delete
 * danger zone. These were previously on the combined Operate surface; the navigation-
 * IA rework split Operate into Audience (people + sending) and Basics (setup here).
 * Content-design (sources, the live issue) lives on the sibling Studio, so nothing
 * here touches the sources list or the drafted issue.
 */
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Notification } from '@nuff/ui/react';

import { deleteNewsletter } from '../../api';
import { LaunchPanel, SetupDisclosure, selectionKeyOf } from './panels';
import { useNewsletterWorkspace } from './workspace';

export function BasicsView(): React.JSX.Element {
  const { deps, newsletter, setNewsletter, sections } = useNewsletterWorkspace();
  const navigate = useNavigate();

  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // The schedule gate is driven by the SELECTED sections (curated in the Studio), so an
  // owner cannot schedule until they have kept something there.
  const hasSelected = selectionKeyOf(sections).length > 0;

  const handleDelete = useCallback(async (): Promise<void> => {
    if (!window.confirm(`Delete "${newsletter.name}"? This cannot be undone.`)) {
      return;
    }
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteNewsletter(deps, newsletter.newsletterId);
      void navigate('/app');
    } catch (caught) {
      setDeleteError(
        caught instanceof Error ? caught.message : 'Could not delete this newsletter.',
      );
      setDeleting(false);
    }
  }, [deps, navigate, newsletter.name, newsletter.newsletterId]);

  return (
    <div className="nlv__section-narrow">
      <div className="nlv__layout">
        <div className="nlv__setup-area">
          <SetupDisclosure deps={deps} newsletter={newsletter} onUpdated={setNewsletter} />
        </div>

        <div className="nlv__schedule-area">
          {/* Sections are curated in the Studio, which is the sibling index route one
              level up, so the "choose sections" hint points there. */}
          <LaunchPanel
            deps={deps}
            newsletter={newsletter}
            hasSelected={hasSelected}
            onUpdated={setNewsletter}
            designTo=".."
          />
        </div>
      </div>

      <div className="dash__danger">
        <span className="auth__label">Danger zone</span>
        <Button
          variant="danger"
          className="app__btn-start"
          onClick={() => void handleDelete()}
          disabled={deleting}
        >
          {deleting ? 'Deleting…' : 'Delete this newsletter'}
        </Button>
        {deleteError !== null && <Notification variant="error">{deleteError}</Notification>}
      </div>
    </div>
  );
}
